import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Input } from "./form";
import { useRouter } from "next/router";
import axios from "axios";
import JoinWhatsAppModal from "./JoinWhatsappModal";
import {
  CalendarDaysIcon,
  CalendarIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { BsAlarm, BsClock } from "react-icons/bs";
import webengage from "../webengage";
import { formatNumber } from "./bootcamp/BootcampTabs";
import mixpanel from "@/utils/mixpanel";

const daysOfWeek = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export default function LeadFormMasterclass({
  coverImage,
  masterclassTitle,
  teacherName,
  category,
  masterclassId,
  activeSlotInfo,
  review,
  starRating,
}: {
  review: number;
  starRating: number;
  coverImage: string;
  masterclassTitle: string;
  teacherName: string;
  category: string;
  masterclassId: string;
  activeSlotInfo: any;
}) {
  const [userData, setUserData] = useState({});
  const [showJoiningPopup, setShowJoiningPopup] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const userDetails = JSON.parse(
      String(localStorage.getItem("tfu-user-auth"))
    );
    if (userDetails) {
      reset({
        name: userDetails.name,
        email: userDetails.email,
        phone: userDetails.phone,
      });
    }
  }, []);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm();

  const submitForm = (data: any) => {
    const { name, email, phone, consent } = data;

    setUserData({
      name: data.name,
      phone: data.phone,
      email: data.email,
    });

    const _webengage = webengage();
    if (_webengage) {
      _webengage.user.login(data.phone);
      _webengage.user.setAttribute("we_email", data.email);
      _webengage.user.setAttribute("we_phone", data.phone);
      _webengage.user.setAttribute("we_first_name", data.name);
      _webengage.track("Masterclass Registered", {
        title: masterclassTitle,
        industry: category,
        instructor: teacherName,
        price: "free",
        date:
          activeSlotInfo?.startDateTime &&
          new Date(activeSlotInfo?.startDateTime),
        source: "Astrolearn",
      });
    }

    if (name && email && phone) {
      axios
        .post(`${process.env.NEXT_PUBLIC_BASE_API}leads`, {
          email,
          phone,
          name,
          consent,
          source: "website",
          comment: "",
          masterclassSlotId: activeSlotInfo.id,
          masterclassId: masterclassId,
        })
        .then((res) => {
          const info = localStorage["tfu-user-auth"]
            ? JSON.parse(localStorage["tfu-user-auth"])
            : null;
          reset();
          setShowJoiningPopup(true);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  };
  const initialTimeInSeconds = 10 * 60; // 4 minutes
  const [seconds, setSeconds] = useState(initialTimeInSeconds);

  useEffect(() => {
    const timer = setInterval(() => {
      if (seconds > 0) {
        setSeconds((prevSeconds) => prevSeconds - 1);
      }
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [seconds]);

  const formatTime = (timeInSeconds: number): string => {
    const minutes = Math.floor(timeInSeconds / 60);
    const remainingSeconds = timeInSeconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (formRef.current) {
      formRef.current.addEventListener("click", (e) => {
        if (e.target instanceof HTMLInputElement) {
          const _webengage = webengage();
          if (_webengage) {
            _webengage.track("lead form edited", {
              title: masterclassTitle,
              industry: category,
              instructor: teacherName,
              price: "free",
              date:
                activeSlotInfo?.startDateTime &&
                new Date(activeSlotInfo?.startDateTime),
              source: "Astrolearn",
              field: e.target.name,
            });
          }
        }
      });
    }
  }, [formRef]);

  return (
    <div className="flex flex-col md:bg-white text-white max-w-3xl rounded-2xl pb-4 w-full md:py-[24px] md:px-[14px]">
      <JoinWhatsAppModal
        isOpen={showJoiningPopup}
        onClose={() => {
          mixpanel.track("ignore_whatsapp", {
            page_name: masterclassTitle,
          });
          setShowJoiningPopup(false);
        }}
        joiningLink={activeSlotInfo?.whatsappGroupLink}
        masterclass={masterclassTitle}
      />
      <img src={coverImage} alt="" className="rounded-t-2xl lg:hidden" />
      <div className="flex flex-col gap-1 w-full md:hidden">
        <div className="flex flex-row text-black border-b-[1px] mt-[16px] items-center border-dashed border-[#ACACAC] border-opacity-50 justify-between w-full px-1 pb-[8px]">
          <div className="flex flex-row items-center gap-2 ">
            <CalendarDaysIcon className="w-[17px] h-[17px]" />
            <p className="font-lato font-[500] text-[14.4px]">
              {dayjs(activeSlotInfo?.startDateTime).format("DD MMMM (dddd)")}
            </p>
          </div>
          <div className="flex flex-row items-center justify-center">
            <ClockIcon className="w-[17px] h-[17px]" />
            <p className="font-lato text-[14.4px] ml-[2px] uppercase">
              {dayjs(activeSlotInfo?.startDateTime).format("hh:mm a")}
            </p>
          </div>
        </div>
        <div className="flex flex-row text-black items-center align-middle justify-between w-full mt-[8px]">
          <div className="w-auto border-none bg-[#9A70261A] bg-op-[10%] rounded-full">
            <p className="font-lato font-[500] text-[15px] text-[#9A7026] px-2 py-1">
              {"Spirituality"}
            </p>
          </div>
          <div className="flex flex-row justify-center place-content-evenly items-center">
            <div className="flex flex-row items-center justify-center">
              {[...Array(Math.round(starRating > 0 ? starRating : 4))].map(
                () => {
                  return (
                    <img
                      src="/astro/star-filled.svg"
                      className="h-[14px] w-[14px]"
                    ></img>
                  );
                }
              )}
              {[...Array(5 - Math.round(starRating > 0 ? starRating : 4))].map(
                () => {
                  return (
                    <img
                      src="/astro/star-empty.svg"
                      className="h-[14px] w-[14px]"
                    ></img>
                  );
                }
              )}
            </div>
            <p className="font-lato text-[16px] ml-[4px]">
              {" "}
              {starRating > 0 ? starRating : 4} ({formatNumber(review)}+)
            </p>
          </div>
        </div>
        <div className="flex flex-row text-black justify-between items-center mt-[8px] w-full px-1">
          <div className=" flex flex-row items-center justify-center space-x-[7.2px]">
            <p className="text-[18px] font-lato text-[#9A7026] font-[600]">
              Free
            </p>
            <p className="line-through fiont-lato text-[14px] font-[400]">
              ₹799
            </p>
          </div>
          <p className="font-lato text-[14.4px] ">
            Offer Ends in{" "}
            <span className="text-[#9A7026] font-[600]">
              {formatTime(seconds)}
            </span>
          </p>
        </div>
      </div>
      <div className="hidden md:flex md:flex-col">
        <div className="flex flex-row justify-between items-center w-full px-[9px]">
          <div className="flex flex-row items-center space-x-[8px]">
            <span className="text-[24px] font-[600] font-lato text-[#9A7026]">
              Free
            </span>
            <span className=" line-through text-[#8C94A3] text-[16px] font-lato font-[400]">
              ₹499
            </span>
          </div>
          <div className="flex bg-[#FFEEE8] text-[#9A7026] px-[12px] py-[8px] text-[14px] font-lato font-[500]">
            100% OFF
          </div>
        </div>
        <div className="flex flex-row justify-between mt-[16.2px]">
          <div className="flex flex-row space-x-[8px]">
            <BsAlarm className="w-[20px] h-[20px] fill-[#9A7026]" />
            <span className="text-[#9A7026] font-[500] font-lato text-[14px]">
              Offer Ends in {formatTime(seconds)}
            </span>
          </div>
          <div className="flex flex-row justify-center place-content-evenly items-center">
            <div className="flex flex-row items-center justify-center">
              {[...Array(Math.round(starRating > 0 ? starRating : 4))].map(
                () => {
                  return (
                    <img
                      src="/astro/star-filled.svg"
                      className="h-[13px] w-[13px]"
                    ></img>
                  );
                }
              )}
              {[...Array(5 - Math.round(starRating > 0 ? starRating : 4))].map(
                () => {
                  return (
                    <img
                      src="/astro/star-empty.svg"
                      className="h-[13px] w-[13px]"
                    ></img>
                  );
                }
              )}
            </div>
            <p className="font-lato text-[13.1px] font-[400] ml-[4px] text-[#9A7026]">
              {" "}
              {starRating > 0 ? starRating : 4} ({formatNumber(review)}+)
            </p>
          </div>
        </div>
      </div>
      <span className="text-[17.4px] mt-[21.5px] font-lato font-[600] md:font-[400] text-black text-left">
        Please enter your details
      </span>
      <form
        onSubmit={handleSubmit(submitForm)}
        ref={formRef}
        className="w-full flex flex-col text-black justify-center mt-[16px] space-y-[16px]"
      >
        <div className="flex flex-col w-full">
          <span className="font-lato text-[13px] md:mb-[6px] ">Full Name</span>
          <Input
            type="text"
            name="name"
            placeholder="Enter your full name"
            register={register}
            errors={errors}
            rules={{ required: true }}
            className="!h-[41.2px] text-black md:h-8 font-lato !text-[15.03px]"
            outerclassName="!w-full"
          />
        </div>

        <div className="flex flex-col w-full">
          <span className="font-lato text-[13px] md:mb-[6px] ">Email</span>
          <Input
            type="email"
            name="email"
            placeholder="Enter your email"
            register={register}
            errors={errors}
            rules={{ required: true }}
            className="  !h-[41.2px] !text-black md:h-8 !font-lato !text-[15.03px]"
            outerclassName="!w-full"
          />
        </div>
        <div className="flex flex-col w-full">
          <span className="font-lato text-[13px] ">Phone Number</span>
          <Input
            type="number"
            name="phone"
            placeholder="Enter your 10 digits number"
            register={register}
            errors={errors}
            outerclassName="!w-full"
            className="!h-[41.2px] text-black md:h-8 font-lato !text-[15.03px] block md:hidden"
            rules={{
              required: true,
              maxLength: {
                value: 10,
                message: "Invalid Phone Number",
              },
              minLength: {
                value: 10,
                message: "Invalid Phone Number",
              },
            }}
          />
        </div>
        <div className="flex w-full gap-2">
          <Input
            type="checkbox"
            name="consent"
            placeholder=""
            register={register}
            errors={errors}
            outerclassName="!w-fit"
            className=""
          />
          <label className="normal-case">
            I authorize Astrolearn to reach out to me with updates and
            notifications via Email, SMS, WhatsApp and RCS.
          </label>
        </div>
        <div className="flex flex-row items-center justify-center">
          <button
            className="bg-[#0A2133] w-full font-[700] px-[24px] py-[14px] mt-[3px] rounded-[4px] text-[18px] text-white border-none font-lato "
            type="submit"
            onClick={() =>
              mixpanel.track("register_now", {
                masterclass: masterclassTitle && masterclassTitle,
                button_location: "bottom_cta",
              })
            }
          >
            {isSubmitting ? "Loading..." : "Register For Masterclass For Free"}
          </button>
        </div>
      </form>
      <div className=" hidden md:block border-t-[1px] border-[#d7d7d7] border-dashed pt-[24px] mt-[24px]">
        <div className="flex flex-row justify-between">
          <div className="flex flex-row space-x-[8px]">
            <CalendarIcon className="w-[24px] h-[24px] stroke-[#6E7485]" />
            <span className="text-[#1D2026] font-lato font-[400] text-[14px]">
              Upcoming Classes
            </span>
          </div>
          <span className="font-lato font-[400] text-[14px] text-[#6E7485]">
            {dayjs(activeSlotInfo?.startDateTime).format("DD MMM (dddd)")}
          </span>
        </div>
        <div className="flex flex-row justify-between mt-[16px]">
          <div className="flex flex-row space-x-[8px]">
            <BsClock className="w-[22px] h-[22px] text-[#6E7485]" />
            <span className="text-[#1D2026] font-lato font-[400] text-[14px]">
              Class Time
            </span>
          </div>
          <span className="font-lato font-[400] text-[14px] text-[#6E7485]">
            {dayjs(activeSlotInfo?.startDateTime).format("hh:mm A")}
          </span>
        </div>
      </div>
    </div>
  );
}
